import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { dramas } from "@/mocks/dramas";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";

export default function Play() {
  const { id } = useParams<{ id: string }>();
  const drama = dramas.find((item) => item.id === id);
  const [currentEpisode, setCurrentEpisode] = useState(1);
  const [notice, setNotice] = useState("");

  usePageMeta(
    drama
      ? `${drama.title}在线播放演示 - 仙剑奇侠传影视站`
      : "播放演示 - 仙剑奇侠传影视站",
    drama
      ? `${drama.title}播放界面演示，可查看剧集集数信息与剧情简述。本页面为纯前端 UI 演示，暂不支持真实视频播放。`
      : "仙剑奇侠传影视站播放界面演示页面。",
    drama ? `${drama.title}播放,${drama.title}集数,仙剑奇侠传在线播放` : "仙剑奇侠传,仙侠剧"
  );

  const episodeList = useMemo(() => {
    if (!drama) return [];
    const total = Math.min(drama.episodes, 12);
    return Array.from({ length: total }, (_, index) => index + 1);
  }, [drama]);

  const recommendations = useMemo(() => {
    if (!drama) return [];
    return dramas.filter((item) => item.id !== drama.id).slice(0, 4);
  }, [drama]);

  const flashNotice = (message: string) => {
    setNotice(message);
    window.setTimeout(() => setNotice(""), 2600);
  };

  if (!drama) {
    return (
      <div className="w-full px-4 md:px-8 py-24 text-center">
        <span className="mx-auto w-16 h-16 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center text-3xl">
          <i className="ri-play-circle-line"></i>
        </span>
        <h1 className="mt-6 font-heading text-2xl text-foreground-950">暂无播放资源</h1>
        <p className="mt-3 text-sm text-foreground-500">
          未找到对应的剧集信息，本页面仅用于播放界面演示。
        </p>
        <Link
          to="/category"
          className="mt-8 inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-700 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          返回电视剧分类
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="w-full px-4 md:px-8 pt-6 md:pt-8">
        <Breadcrumb
          items={[
            { label: "电视剧分类", to: "/category" },
            { label: drama.title, to: `/detail/${drama.id}` },
            { label: "播放演示" },
          ]}
        />
      </div>

      <div className="w-full px-4 md:px-8 py-6 md:py-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8">
          <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-primary-950">
            <img
              src={drama.banner}
              alt={`${drama.title}播放画面演示`}
              className="absolute inset-0 w-full h-full object-cover object-top opacity-70"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/85 via-primary-950/40 to-primary-950/30"></div>

            <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500 text-foreground-950 text-xs font-medium">
              <i className="ri-information-line"></i>
              演示模式 · 非真实播放
            </span>

            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
              <button
                type="button"
                onClick={() => flashNotice("当前为前端播放界面演示，暂未接入真实视频资源。")}
                className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur text-white flex items-center justify-center text-3xl md:text-4xl transition-colors cursor-pointer"
                aria-label="播放（演示）"
              >
                <i className="ri-play-fill"></i>
              </button>
              <h2 className="mt-6 font-heading text-xl md:text-2xl font-semibold text-white">
                {drama.title} · 第 {currentEpisode} 集
              </h2>
              <p className="mt-2 text-xs md:text-sm text-background-200 max-w-lg">
                本页面为纯前端 UI 演示，用于展示播放界面与集数切换效果，不提供真实视频播放。
              </p>
            </div>

            <div className="absolute inset-x-0 bottom-0 p-4 md:p-5">
              <div className="h-1 w-full rounded-full bg-white/25 overflow-hidden">
                <div className="h-full w-1/4 rounded-full bg-accent-400"></div>
              </div>
              <div className="mt-2 flex items-center justify-between text-[11px] text-background-200">
                <span>00:00</span>
                <span>--:--</span>
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h1 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">
                {drama.title}
              </h1>
              <Link
                to={`/detail/${drama.id}`}
                className="inline-flex items-center gap-2 h-10 px-5 rounded-md border border-background-300 text-foreground-800 hover:border-primary-400 hover:text-primary-700 text-sm whitespace-nowrap transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回剧集详情
              </Link>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
              <span className="flex items-center gap-1.5">
                <i className="ri-play-list-line text-primary-700"></i>
                正在播放：第 {currentEpisode} 集
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-film-line text-primary-700"></i>
                全 {drama.episodes} 集
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-primary-700"></i>
                {drama.year} 年
              </span>
            </div>

            <h3 className="mt-6 text-sm font-medium text-foreground-900">剧情简述</h3>
            <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
              {drama.summary}
            </p>
            <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
              {drama.synopsis[0]}
            </p>
          </div>
        </div>

        <aside className="lg:col-span-4">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-list-check-2 text-primary-700"></i>
              选集
            </h2>
            <div className="grid grid-cols-4 gap-2">
              {episodeList.map((episode) => (
                <button
                  key={episode}
                  type="button"
                  onClick={() => {
                    setCurrentEpisode(episode);
                    flashNotice(`已切换到第 ${episode} 集（演示）`);
                  }}
                  className={`h-10 rounded-md text-sm whitespace-nowrap transition-colors cursor-pointer ${
                    episode === currentEpisode
                      ? "bg-primary-700 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-secondary-100 hover:text-secondary-900"
                  }`}
                >
                  {episode}
                </button>
              ))}
            </div>
            <p className="mt-3 text-xs text-foreground-400">
              演示仅展示前 {episodeList.length} 集，真实播放需后续接入视频资源。
            </p>
          </div>

          <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-movie-line text-primary-700"></i>
              相关推荐
            </h2>
            <ul className="flex flex-col gap-3">
              {recommendations.map((item) => (
                <li key={item.id}>
                  <Link
                    to={`/detail/${item.id}`}
                    className="group flex items-center gap-3 p-2 rounded-md hover:bg-primary-50 transition-colors"
                  >
                    <span className="w-12 h-14 rounded-md overflow-hidden shrink-0 bg-background-200">
                      <img
                        src={item.poster}
                        alt={`${item.title}海报`}
                        className="w-full h-full object-cover object-top"
                      />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm text-foreground-900 truncate group-hover:text-primary-700 transition-colors">
                        {item.title}
                      </span>
                      <span className="block text-xs text-foreground-500 mt-1">
                        {item.year} · {item.category}
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>

      {notice && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[60] px-5 py-3 rounded-md bg-foreground-950 text-background-50 text-sm animate-fade-in">
          {notice}
        </div>
      )}
    </div>
  );
}